/**
 * Shared low-level primitives for fraction-digit normalization across UI and CSV
 * number formatting modules. Internal — prefer the higher-level helpers in
 * {@link ./numberFormat} (UI) or {@link ./csvNumberFormat} (CSV interchange).
 *
 * Centralizing these constants here resolves the reinvention pattern called out
 * in Epic **11.1.9** (do not re-implement the same rounding clamps in every
 * formatter module).
 */

/**
 * Hard upper bound when callers pass an explicit `maximumFractionDigits` greater
 * than the helper's default. Mirrors the safety cap used by `Intl.NumberFormat`
 * implementations and prevents accidental "20+ digit" exports.
 */
export const MAX_FRACTION_DIGITS_CAP = 20;

/** Em-dash placeholder for non-displayable numeric inputs (null / NaN / Infinity / empty). */
export const DECIMAL_PLACEHOLDER = "—";

export interface FractionDigitOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Resolve `Intl.NumberFormat`-compatible `min` / `max` digit values from caller
 * options. Defaults to **0..2** (Epic **11.1** baseline), clamps `max` to
 * {@link MAX_FRACTION_DIGITS_CAP}, and guarantees `min <= max` for invalid
 * combinations such as `{ min: 4, max: 1 }`.
 */
export function resolveIntlFractionDigits(
  options: FractionDigitOptions | undefined,
): { min: number; max: number } {
  const defaultMax = 2;
  const rawMax =
    options?.maximumFractionDigits !== undefined
      ? Math.min(
          Math.max(options.maximumFractionDigits, 0),
          MAX_FRACTION_DIGITS_CAP,
        )
      : defaultMax;
  const rawMin =
    options?.minimumFractionDigits !== undefined
      ? Math.max(options.minimumFractionDigits, 0)
      : 0;
  const min = Math.min(rawMin, rawMax);
  const max = Math.max(min, rawMax);
  return { min, max };
}

/**
 * Whether `value` can be coerced to a finite, non-empty number suitable for
 * `Intl.NumberFormat`. Empty strings, `null`, `undefined`, `NaN`, and `±Infinity`
 * are rejected so the caller can return {@link DECIMAL_PLACEHOLDER}.
 */
export function isDisplayableNumber(
  value: number | string | null | undefined,
): value is number | string {
  if (value === null || value === undefined) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  const n = Number(value);
  return Number.isFinite(n);
}
